import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  createOrganizationRole,
  deleteOrganizationRole,
  fetchMembershipCountsByRole,
  listOrganizationRoles,
  setOrganizationRolePermissions,
  standardRoleMeta,
  updateOrganizationRole,
  type CoreRoleRow,
} from '@/lib/coreRoles';
import {
  PERMISSION_MODULES,
  PERMISSION_SCOPES,
  checkResourceAccess,
  createAccessScope,
  createCorePermission,
  deleteAccessScope,
  deleteCorePermission,
  listAccessScopes,
  listCorePermissions,
  permissionModuleMeta,
  permissionScopeMeta,
  type CoreAccessScope,
  type CorePermission,
} from '@/lib/corePermissions';
import Modal from '@/pages/dashboard/core/components/Modal';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';
const SUB_TABS = [
  { key: 'roles', label: 'Rôles', icon: 'ri-user-settings-line' },
  { key: 'permissions', label: 'Permissions', icon: 'ri-shield-keyhole-line' },
  { key: 'access', label: 'Accès aux ressources', icon: 'ri-focus-3-line' },
] as const;
type SubTab = (typeof SUB_TABS)[number]['key'];

export default function RolesPermissionsTab({ org }: Props) {
  const [sub, setSub] = useState<SubTab>('roles');
  const [roles, setRoles] = useState<CoreRoleRow[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [perms, setPerms] = useState<CorePermission[]>([]);
  const [scopes, setScopes] = useState<CoreAccessScope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleForm, setRoleForm] = useState<{ id: number | null; name: string; description: string; perms: string[] }>({ id: null, name: '', description: '', perms: [] });
  const [permOpen, setPermOpen] = useState(false);
  const [permForm, setPermForm] = useState({ key: '', module: 'customer', resource: '', action: 'read', label: '' });
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeForm, setScopeForm] = useState({ userId: '', locationId: '', module: '', resourceType: '', resourceId: '', canRead: true, canWrite: false, canDelete: false });
  const [testForm, setTestForm] = useState({ userId: '', resourceType: '', resourceId: '', action: 'read' });
  const [testResult, setTestResult] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [r, c, p, s] = await Promise.all([
        listOrganizationRoles(org.id),
        fetchMembershipCountsByRole(org.id),
        listCorePermissions(),
        listAccessScopes(org.id),
      ]);
      setRoles(r);
      setCounts(c);
      setPerms(p);
      setScopes(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const permsByModule = useMemo(() => {
    const map: Record<string, CorePermission[]> = {};
    perms.forEach((p) => {
      const m = p.module || 'other';
      if (!map[m]) map[m] = [];
      map[m].push(p);
    });
    return map;
  }, [perms]);

  const standardKeys = useMemo(() => new Set(PERMISSION_MODULES.map((m) => m.key)), []);

  const openCreateRole = () => { setRoleForm({ id: null, name: '', description: '', perms: [] }); setRoleOpen(true); };
  const openEditRole = (r: CoreRoleRow) => { setRoleForm({ id: r.id, name: r.name, description: r.description || '', perms: r.permissions }); setRoleOpen(true); };

  const saveRole = async () => {
    if (!roleForm.name.trim()) return;
    setSaving(true);
    try {
      if (roleForm.id) {
        await updateOrganizationRole(roleForm.id, { name: roleForm.name.trim(), description: roleForm.description.trim() });
        await setOrganizationRolePermissions(roleForm.id, roleForm.perms);
      } else {
        await createOrganizationRole({ tenantId: org.tenant_id, organizationId: org.id, name: roleForm.name.trim(), description: roleForm.description.trim(), permissions: roleForm.perms });
      }
      setRoleOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  const removeRole = async (r: CoreRoleRow) => {
    setBusyId(r.id);
    try { await deleteOrganizationRole(r.id); load(); } finally { setBusyId(null); }
  };

  const savePerm = async () => {
    if (!permForm.key.trim()) return;
    setSaving(true);
    try {
      await createCorePermission({ key: permForm.key.trim(), module: permForm.module, resource: permForm.resource.trim() || permForm.module, action: permForm.action.trim() || 'read', label: permForm.label.trim() || permForm.key.trim() });
      setPermOpen(false);
      setPermForm({ key: '', module: 'customer', resource: '', action: 'read', label: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  const removePerm = async (p: CorePermission) => {
    setBusyId(p.id);
    try { await deleteCorePermission(p.id); load(); } finally { setBusyId(null); }
  };

  const saveScope = async () => {
    const uid = Number(testForm.userId);
    if (!scopeForm.userId.trim()) return;
    setSaving(true);
    try {
      await createAccessScope({
        tenantId: org.tenant_id,
        organizationId: org.id,
        userId: Number(scopeForm.userId),
        locationId: scopeForm.locationId.trim() ? Number(scopeForm.locationId) : null,
        module: scopeForm.module || null,
        resourceType: scopeForm.resourceType || null,
        resourceId: scopeForm.resourceId.trim() || null,
        canRead: scopeForm.canRead,
        canWrite: scopeForm.canWrite,
        canDelete: scopeForm.canDelete,
      });
      void uid;
      setScopeOpen(false);
      setScopeForm({ userId: '', locationId: '', module: '', resourceType: '', resourceId: '', canRead: true, canWrite: false, canDelete: false });
      load();
    } finally {
      setSaving(false);
    }
  };

  const removeScope = async (s: CoreAccessScope) => {
    setBusyId(s.id);
    try { await deleteAccessScope(s.id); load(); } finally { setBusyId(null); }
  };

  const runTest = async () => {
    if (!testForm.userId.trim()) return;
    const allowed = await checkResourceAccess({
      userId: Number(testForm.userId),
      organizationId: org.id,
      resourceType: testForm.resourceType || null,
      resourceId: testForm.resourceId.trim() || null,
      action: testForm.action as 'read' | 'write' | 'delete',
    });
    setTestResult(allowed);
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        Rôles &amp; Permissions : 11 rôles standards + rôles personnalisés, permissions granulaires (customer.read, invoice.create…)
        applicables globalement, par entreprise, par établissement, par module ou par ressource. La sécurité ne s’arrête pas au rôle :
        on vérifie toujours <span className="font-semibold text-foreground-700">User → Business → Branch → Resource</span>.
      </p>

      <div className="flex flex-wrap items-center gap-1 bg-background-100 rounded-full p-1 w-fit">
        {SUB_TABS.map((t) => (
          <button key={t.key} onClick={() => setSub(t.key)} className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${sub === t.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            <i className={t.icon}></i>{t.label}
          </button>
        ))}
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : sub === 'roles' ? (
        <>
          <div className="flex justify-end">
            <button onClick={openCreateRole} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"><i className="ri-add-line"></i> Rôle personnalisé</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {roles.map((r) => {
              const meta = standardRoleMeta(r.role_key);
              const memberCount = counts[r.role_key] || 0;
              return (
                <div key={r.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0"><i className={`${meta?.icon || 'ri-user-settings-line'} text-foreground-600`}></i></span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-foreground-900 truncate">{r.name}</p>
                        <p className="text-[11px] text-foreground-400 truncate">niveau {r.level}{r.is_system ? ' · standard' : ' · personnalisé'}</p>
                      </div>
                    </div>
                    {!r.is_system && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button onClick={() => openEditRole(r)} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"><i className="ri-edit-line"></i></button>
                        <button onClick={() => removeRole(r)} disabled={busyId === r.id} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-delete-bin-line"></i></button>
                      </div>
                    )}
                  </div>
                  {r.description && <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{r.description}</p>}
                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 whitespace-nowrap">{r.permissions.length} permission(s)</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-900 whitespace-nowrap">{memberCount} membre(s)</span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : sub === 'permissions' ? (
        <>
          <div className="flex justify-end">
            <button onClick={() => setPermOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"><i className="ri-add-line"></i> Permission</button>
          </div>
          <div className="space-y-3">
            {Object.entries(permsByModule).map(([mod, list]) => (
              <div key={mod} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 border-b border-background-200/60 flex items-center gap-2">
                  <i className={`${permissionModuleMeta(mod).icon} text-foreground-600`}></i>
                  <h4 className="text-sm font-semibold text-foreground-900">{permissionModuleMeta(mod).label}</h4>
                  <span className="text-[11px] text-foreground-400">{list.length}</span>
                </div>
                <div className="flex flex-wrap gap-2 p-3">
                  {list.map((p) => (
                    <span key={p.id} className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full border border-background-200/70 bg-background-100 text-xs text-foreground-700">
                      <code className="text-[11px]">{p.key}</code>
                      {!standardKeys.has(p.module || '') || !p.key.includes('.') ? null : null}
                      <button onClick={() => removePerm(p)} disabled={busyId === p.id} className="w-5 h-5 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"><i className="ri-close-line text-[11px]"></i></button>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {PERMISSION_SCOPES.map((s) => (
              <div key={s.key} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
                <i className={`${s.icon} text-lg text-primary-600`}></i>
                <p className="text-xs font-medium text-foreground-700 mt-1.5">{s.label}</p>
                <p className="text-[11px] text-foreground-400">{s.desc}</p>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-foreground-700">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 whitespace-nowrap"><i className="ri-user-3-line"></i> Utilisateur</span>
              <i className="ri-arrow-right-line text-foreground-400"></i>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 whitespace-nowrap"><i className="ri-building-2-line"></i> Entreprise</span>
              <i className="ri-arrow-right-line text-foreground-400"></i>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 whitespace-nowrap"><i className="ri-map-pin-line"></i> Établissement</span>
              <i className="ri-arrow-right-line text-foreground-400"></i>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50 whitespace-nowrap"><i className="ri-focus-3-line"></i> Ressource</span>
            </div>
            <p className="text-[11px] text-foreground-500 mt-2">Un manager de Fès n’a pas nécessairement accès aux données de l’établissement de Casablanca : chaque périmètre est vérifié ressource par ressource.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-foreground-900 mb-3">Tester un accès</h4>
              <div className="space-y-2">
                <input className={INPUT} value={testForm.userId} onChange={(e) => setTestForm({ ...testForm, userId: e.target.value })} placeholder="ID utilisateur" />
                <div className="grid grid-cols-2 gap-2">
                  <input className={INPUT} value={testForm.resourceType} onChange={(e) => setTestForm({ ...testForm, resourceType: e.target.value })} placeholder="Type de ressource" />
                  <input className={INPUT} value={testForm.resourceId} onChange={(e) => setTestForm({ ...testForm, resourceId: e.target.value })} placeholder="ID ressource" />
                </div>
                <select className={INPUT} value={testForm.action} onChange={(e) => setTestForm({ ...testForm, action: e.target.value })}>
                  <option value="read">Lire</option>
                  <option value="write">Écrire</option>
                  <option value="delete">Supprimer</option>
                </select>
                <button onClick={runTest} className="w-full px-4 py-2 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer">Vérifier l’accès</button>
                {testResult !== null && (
                  <div className={`p-2.5 rounded-md text-xs font-medium ${testResult ? 'bg-primary-50 text-primary-700 border border-primary-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    <i className={testResult ? 'ri-checkbox-circle-line' : 'ri-close-circle-line'}></i> {testResult ? 'Accès autorisé' : 'Accès refusé'}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-foreground-900">Périmètres configurés</h4>
                <button onClick={() => setScopeOpen(true)} className="px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors cursor-pointer"><i className="ri-add-line"></i> Ajouter</button>
              </div>
              {scopes.length === 0 ? (
                <p className="text-xs text-foreground-400 text-center py-6">Aucun périmètre. Ajoutez-en pour limiter l’accès par établissement ou ressource.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {scopes.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 bg-background-100/60 rounded-md p-2.5">
                      <i className="ri-user-3-line text-foreground-500"></i>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-foreground-800 truncate">Utilisateur #{s.user_id}{s.location_id ? ` · Établ. #${s.location_id}` : ''}</p>
                        <p className="text-[11px] text-foreground-400 truncate">
                          {s.module || 'tous modules'}{s.resource_type ? ` · ${s.resource_type}` : ''}{s.resource_id ? ` #${s.resource_id}` : ''}
                          {' · '}{[s.can_read && 'lire', s.can_write && 'écrire', s.can_delete && 'suppr.'].filter(Boolean).join(', ')}
                        </p>
                      </div>
                      <button onClick={() => removeScope(s)} disabled={busyId === s.id} className="w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-50 flex-shrink-0"><i className="ri-delete-bin-line"></i></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {roleOpen && (
        <Modal
          title={roleForm.id ? 'Modifier le rôle' : 'Nouveau rôle personnalisé'}
          onClose={() => setRoleOpen(false)}
          maxWidth="max-w-2xl"
          footer={
            <>
              <button onClick={() => setRoleOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={saveRole} disabled={saving || !roleForm.name.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Enregistrer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom du rôle</span>
              <input className={INPUT} value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })} placeholder="Ex : Responsable établissement" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Description (optionnel)</span>
              <input className={INPUT} value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} placeholder="Rôle résumé en une phrase" />
            </label>
            <div>
              <span className="block text-xs font-medium text-foreground-600 mb-2">Permissions</span>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {Object.entries(permsByModule).map(([mod, list]) => (
                  <div key={mod} className="border border-background-200/70 rounded-md p-2.5">
                    <p className="text-[11px] font-semibold text-foreground-600 mb-1.5">{permissionModuleMeta(mod).label}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {list.map((p) => {
                        const on = roleForm.perms.includes(p.key);
                        return (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => setRoleForm({ ...roleForm, perms: on ? roleForm.perms.filter((x) => x !== p.key) : [...roleForm.perms, p.key] })}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors cursor-pointer whitespace-nowrap ${on ? 'bg-primary-500 text-background-50 border-primary-500' : 'bg-background-50 text-foreground-600 border-background-200/70 hover:bg-background-100'}`}
                          >
                            {on && <i className="ri-check-line"></i>}{p.key}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {permOpen && (
        <Modal
          title="Nouvelle permission"
          onClose={() => setPermOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setPermOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={savePerm} disabled={saving || !permForm.key.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Créer
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Clé (module.action)</span>
              <input className={INPUT} value={permForm.key} onChange={(e) => setPermForm({ ...permForm, key: e.target.value })} placeholder="Ex : project.update" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Module</span>
              <select className={INPUT} value={permForm.module} onChange={(e) => setPermForm({ ...permForm, module: e.target.value })}>
                {PERMISSION_MODULES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Ressource</span>
                <input className={INPUT} value={permForm.resource} onChange={(e) => setPermForm({ ...permForm, resource: e.target.value })} placeholder="Ex : project" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Action</span>
                <input className={INPUT} value={permForm.action} onChange={(e) => setPermForm({ ...permForm, action: e.target.value })} placeholder="Ex : update" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Libellé</span>
              <input className={INPUT} value={permForm.label} onChange={(e) => setPermForm({ ...permForm, label: e.target.value })} placeholder="Ex : Modifier un projet" />
            </label>
          </div>
        </Modal>
      )}

      {scopeOpen && (
        <Modal
          title="Nouveau périmètre d’accès"
          onClose={() => setScopeOpen(false)}
          maxWidth="max-w-lg"
          footer={
            <>
              <button onClick={() => setScopeOpen(false)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={saveScope} disabled={saving || !scopeForm.userId.trim()} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>} Ajouter
              </button>
            </>
          }
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">ID utilisateur</span>
                <input className={INPUT} value={scopeForm.userId} onChange={(e) => setScopeForm({ ...scopeForm, userId: e.target.value })} placeholder="Ex : 100234" />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">ID établissement (optionnel)</span>
                <input className={INPUT} value={scopeForm.locationId} onChange={(e) => setScopeForm({ ...scopeForm, locationId: e.target.value })} placeholder="Ex : 2" />
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Module (optionnel)</span>
                <select className={INPUT} value={scopeForm.module} onChange={(e) => setScopeForm({ ...scopeForm, module: e.target.value })}>
                  <option value="">— Tous —</option>
                  {PERMISSION_MODULES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-foreground-600 mb-1">Type de ressource</span>
                <input className={INPUT} value={scopeForm.resourceType} onChange={(e) => setScopeForm({ ...scopeForm, resourceType: e.target.value })} placeholder="Ex : invoice" />
              </label>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">ID ressource (optionnel)</span>
              <input className={INPUT} value={scopeForm.resourceId} onChange={(e) => setScopeForm({ ...scopeForm, resourceId: e.target.value })} placeholder="Ex : 123" />
            </label>
            <div className="flex flex-wrap gap-3">
              {([['canRead', 'Lire'], ['canWrite', 'Écrire'], ['canDelete', 'Supprimer']] as const).map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={scopeForm[k]} onChange={(e) => setScopeForm({ ...scopeForm, [k]: e.target.checked })} className="w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-300" />
                  <span className="text-sm text-foreground-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}